<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Slideover } from "@/components/Base/Headless";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import { useNotificationsStore, type AppNotification } from "@/stores/notifications";
import { storeToRefs } from "pinia";

const props = withDefaults(
  defineProps<{
    notificationsPanel: boolean;
    setNotificationsPanel: (val: boolean) => void;
  }>(),
  {
    notificationsPanel: false,
    setNotificationsPanel: () => {},
  }
);

const { t, locale } = useI18n();
const router = useRouter();
const store = useNotificationsStore();
const { items, loading, hasUnread } = storeToRefs(store);

const iconFor = (type: string) => {
  if (type === "news") return "Newspaper";
  if (type === "minutes") return "FileText";
  if (type === "fee_new") return "Wallet";
  if (type === "receipt_to_verify") return "FileCheck";
  if (type === "chat") return "MessagesSquare";
  return "Bell";
};

const formatWhen = (value: string | null | undefined) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(locale.value === "es" ? "es-ES" : "gl-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const resolveLink = (n: AppNotification): string | null => {
  const link = (n.link || "").trim();
  if (!link) return null;
  if (link.startsWith("http")) return link;
  return link.startsWith("/") ? link : `/${link}`;
};

const openItem = async (n: AppNotification) => {
  if (!n.is_read) await store.markRead(n.id);
  props.setNotificationsPanel(false);
  const link = resolveLink(n);
  if (!link) return;
  if (link.startsWith("http")) {
    window.location.href = link;
    return;
  }
  router.push(link);
};

const markAll = async () => {
  await store.markAllRead();
};

const close = () => props.setNotificationsPanel(false);

const empty = computed(() => !loading.value && items.value.length === 0);
</script>

<template>
  <div>
    <Slideover :open="props.notificationsPanel" @close="close">
      <Slideover.Panel class="w-72 sm:w-96 rounded-[0.75rem_0_0_0.75rem/1.1rem_0_0_1.1rem]">
        <a
          href=""
          class="focus:outline-none hover:bg-white/10 bg-white/5 transition-all hover:rotate-180 absolute inset-y-0 left-0 right-auto flex items-center justify-center my-auto -ml-[60px] sm:-ml-[105px] border rounded-full text-white/90 w-8 h-8 sm:w-14 sm:h-14 border-white/90 hover:scale-105"
          @click.prevent="close"
        >
          <Lucide class="w-8 h-8 stroke-[1]" icon="X" />
        </a>
        <Slideover.Title class="px-6 py-5">
          <h2 class="mr-auto text-base font-medium">{{ t("notifications.panel_title") }}</h2>
          <Button
            v-if="hasUnread"
            variant="outline-secondary"
            class="hidden sm:flex"
            @click="markAll"
          >
            <Lucide icon="Check" class="w-4 h-4 mr-2" />
            {{ t("notifications.mark_all") }}
          </Button>
        </Slideover.Title>
        <Slideover.Description class="p-0">
          <div v-if="loading && !items.length" class="p-6 text-sm text-slate-500">
            {{ t("common.loading") }}…
          </div>
          <div v-else-if="empty" class="p-6 text-sm text-slate-500 text-center">
            {{ t("notifications.empty") }}
          </div>
          <div v-else class="flex flex-col p-3 gap-0.5 max-h-[70vh] overflow-y-auto">
            <button
              v-for="n in items"
              :key="n.id"
              type="button"
              class="flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-slate-100/80 transition-colors"
              :class="!n.is_read ? 'bg-primary/5' : ''"
              @click="openItem(n)"
            >
              <div
                class="mt-0.5 rounded-full p-2 shrink-0"
                :class="!n.is_read ? 'bg-primary/15 text-primary' : 'bg-slate-100 text-slate-500'"
              >
                <Lucide :icon="iconFor(n.type)" class="w-4 h-4" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="font-medium text-sm text-slate-800 leading-snug">
                  {{ n.title }}
                </div>
                <div v-if="n.body" class="text-slate-500 text-xs mt-0.5 line-clamp-2">
                  {{ n.body }}
                </div>
                <div class="text-[11px] text-slate-400 mt-1">
                  {{ formatWhen(n.created_at) }}
                </div>
              </div>
              <span
                v-if="!n.is_read"
                class="mt-2 w-2 h-2 rounded-full bg-primary shrink-0"
              />
            </button>
          </div>
        </Slideover.Description>
      </Slideover.Panel>
    </Slideover>
  </div>
</template>
